import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;
const ZOOM_FACTOR = 1.12;
export const TrainingStateGraph = ({ graphKey, nodes, edges, defaultPositions, currentNodeId, nodeRadius = 40, hintText, arrowMarkerId }) => {
    const containerRef = useRef(null);
    const dragStateRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [viewTransform, setViewTransform] = useState({ panX: 0, panY: 0, scale: 1 });
    const [hasUserAdjustedView, setHasUserAdjustedView] = useState(false);
    const [positions, setPositions] = useState({});
    const [edgeLabelOffsets, setEdgeLabelOffsets] = useState({});
    const [showEdgeLabels, setShowEdgeLabels] = useState(true);
    useEffect(() => {
        setPositions((previousPositions) => {
            var _a;
            const nextPositions = {};
            for (const node of nodes) {
                nextPositions[node.id] = (_a = previousPositions[node.id]) !== null && _a !== void 0 ? _a : defaultPositions[node.id];
            }
            return nextPositions;
        });
        setEdgeLabelOffsets((previousOffsets) => {
            var _a;
            const nextOffsets = {};
            for (const edge of edges) {
                nextOffsets[edge.id] = (_a = previousOffsets[edge.id]) !== null && _a !== void 0 ? _a : { x: 0, y: 0 };
            }
            return nextOffsets;
        });
    }, [nodes, edges, defaultPositions]);
    useEffect(() => {
        setHasUserAdjustedView(false);
        setViewTransform({ panX: 0, panY: 0, scale: 1 });
        dragStateRef.current = null;
    }, [graphKey]);
    useEffect(() => {
        const container = containerRef.current;
        if (container === null)
            return;
        const updateSize = () => {
            setContainerSize({
                width: container.clientWidth,
                height: container.clientHeight
            });
        };
        updateSize();
        const resizeObserver = new ResizeObserver(() => {
            updateSize();
        });
        resizeObserver.observe(container);
        return () => {
            resizeObserver.disconnect();
        };
    }, []);
    useEffect(() => {
        if (hasUserAdjustedView)
            return;
        if (nodes.length === 0)
            return;
        if (containerSize.width <= 0 || containerSize.height <= 0)
            return;
        if (Object.keys(positions).length === 0)
            return;
        setViewTransform(computeFitViewTransform(nodes, positions, containerSize, nodeRadius));
    }, [nodes, positions, containerSize, hasUserAdjustedView, nodeRadius]);
    useEffect(() => {
        const handleMouseMove = (event) => {
            const dragState = dragStateRef.current;
            if (dragState === null)
                return;
            if (dragState.type === "pan") {
                setViewTransform((previousTransform) => (Object.assign(Object.assign({}, previousTransform), { panX: dragState.startPanX + (event.clientX - dragState.pointerX), panY: dragState.startPanY + (event.clientY - dragState.pointerY) })));
                return;
            }
            const graphPoint = getGraphPoint(event.clientX, event.clientY, containerRef.current, viewTransform);
            if (graphPoint === undefined)
                return;
            if (dragState.type === "edge-label") {
                setEdgeLabelOffsets((previousOffsets) => (Object.assign(Object.assign({}, previousOffsets), { [dragState.edgeId]: {
                        x: graphPoint.x + dragState.pointerOffsetX - dragState.baseAnchorX,
                        y: graphPoint.y + dragState.pointerOffsetY - dragState.baseAnchorY
                    } })));
                return;
            }
            setPositions((previousPositions) => (Object.assign(Object.assign({}, previousPositions), { [dragState.nodeId]: {
                    x: graphPoint.x + dragState.offsetX,
                    y: graphPoint.y + dragState.offsetY
                } })));
        };
        const handleMouseUp = () => {
            dragStateRef.current = null;
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [viewTransform]);
    const renderedEdges = useMemo(() => {
        return edges.map((edge) => {
            var _a;
            const from = positions[edge.fromId];
            const to = positions[edge.toId];
            if (from === undefined || to === undefined)
                return undefined;
            const directionX = to.x - from.x;
            const directionY = to.y - from.y;
            const length = Math.hypot(directionX, directionY) || 1;
            const unitX = directionX / length;
            const unitY = directionY / length;
            const rightX = -unitY;
            const rightY = unitX;
            const edgeOffset = edge.hasReverseEdge ? 8 : 0;
            const startX = from.x + unitX * (nodeRadius + 4) + rightX * edgeOffset;
            const startY = from.y + unitY * (nodeRadius + 4) + rightY * edgeOffset;
            const endX = to.x - unitX * (nodeRadius + 10) + rightX * edgeOffset;
            const endY = to.y - unitY * (nodeRadius + 10) + rightY * edgeOffset;
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;
            const leftOffsetDistance = 26;
            const leftOffsetX = -unitY * leftOffsetDistance;
            const leftOffsetY = unitX * leftOffsetDistance;
            const tangentOffset = (edge.groupIndex - (edge.groupCount - 1) / 2) * 56;
            const tangentOffsetX = unitX * tangentOffset;
            const tangentOffsetY = unitY * tangentOffset;
            const labelOffset = (_a = edgeLabelOffsets[edge.id]) !== null && _a !== void 0 ? _a : { x: 0, y: 0 };
            const baseTextX = midX + leftOffsetX + tangentOffsetX;
            const baseTextY = midY + leftOffsetY + tangentOffsetY;
            const textX = baseTextX + labelOffset.x;
            const textY = baseTextY + labelOffset.y;
            const labelText = edge.labelSegments.map((segment) => segment.text).join("");
            const labelWidth = Math.max(52, labelText.length * 6.8);
            const labelHeight = 20;
            const labelGap = 6;
            const isLabelLeftOfEdge = textX < midX;
            const labelAnchor = isLabelLeftOfEdge ? "end" : "start";
            const anchorX = isLabelLeftOfEdge ? textX + labelGap : textX - labelGap;
            return {
                edge,
                startX,
                startY,
                endX,
                endY,
                midX,
                midY,
                textY,
                labelWidth,
                labelHeight,
                labelAnchor,
                anchorX,
                isLabelLeftOfEdge
            };
        });
    }, [edges, positions, edgeLabelOffsets, nodeRadius]);
    return (React.createElement("div", { ref: containerRef, className: "training-stage-tab", onContextMenu: (event) => {
            event.preventDefault();
        }, onMouseDown: (event) => {
            if (event.button !== 2)
                return;
            setHasUserAdjustedView(true);
            dragStateRef.current = {
                type: "pan",
                pointerX: event.clientX,
                pointerY: event.clientY,
                startPanX: viewTransform.panX,
                startPanY: viewTransform.panY
            };
        }, onWheel: (event) => {
            var _a;
            event.preventDefault();
            const rect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect === undefined)
                return;
            const pointerX = event.clientX - rect.left;
            const pointerY = event.clientY - rect.top;
            const zoomMultiplier = event.deltaY < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR;
            setViewTransform((previousTransform) => {
                const nextScale = clamp(previousTransform.scale * zoomMultiplier, MIN_SCALE, MAX_SCALE);
                if (nextScale === previousTransform.scale)
                    return previousTransform;
                const graphX = (pointerX - previousTransform.panX) / previousTransform.scale;
                const graphY = (pointerY - previousTransform.panY) / previousTransform.scale;
                return {
                    scale: nextScale,
                    panX: pointerX - graphX * nextScale,
                    panY: pointerY - graphY * nextScale
                };
            });
            setHasUserAdjustedView(true);
        } },
        React.createElement("div", { className: "training-stage-hint" }, hintText),
        React.createElement("button", { className: "training-graph-toggle-button", onClick: () => {
                setShowEdgeLabels((previous) => !previous);
            } }, showEdgeLabels ? "Hide Labels" : "Show Labels"),
        React.createElement("svg", { className: "training-stage-canvas" },
            React.createElement("defs", null,
                React.createElement("marker", { id: arrowMarkerId, viewBox: "0 0 10 10", refX: "8", refY: "5", markerWidth: "7", markerHeight: "7", orient: "auto-start-reverse" },
                    React.createElement("path", { d: "M 0 0 L 10 5 L 0 10 z", fill: "#535353" }))),
            React.createElement("g", { transform: `translate(${viewTransform.panX}, ${viewTransform.panY})` },
                React.createElement("g", { transform: `scale(${viewTransform.scale})` },
                    renderedEdges.map((renderedEdge) => {
                        if (renderedEdge === undefined)
                            return null;
                        const { edge, startX, startY, endX, endY, midX, midY, anchorX, textY, isLabelLeftOfEdge, labelAnchor, labelWidth, labelHeight } = renderedEdge;
                        return (React.createElement("g", { key: edge.id },
                            React.createElement("line", { x1: startX, y1: startY, x2: endX, y2: endY, className: "training-stage-edge", markerEnd: `url(#${arrowMarkerId})` }),
                            showEdgeLabels && (React.createElement(React.Fragment, null,
                                React.createElement("line", { x1: midX, y1: midY, x2: anchorX, y2: textY, className: "training-object-edge-connector" }),
                                React.createElement("g", { transform: `translate(${anchorX}, ${textY})`, className: "training-object-edge-label-group", onMouseDown: (event) => {
                                        if (event.button !== 0)
                                            return;
                                        const graphPoint = getGraphPoint(event.clientX, event.clientY, containerRef.current, viewTransform);
                                        if (graphPoint === undefined)
                                            return;
                                        setHasUserAdjustedView(true);
                                        dragStateRef.current = {
                                            type: "edge-label",
                                            edgeId: edge.id,
                                            pointerOffsetX: anchorX - graphPoint.x,
                                            pointerOffsetY: textY - graphPoint.y,
                                            baseAnchorX: anchorX,
                                            baseAnchorY: textY
                                        };
                                        event.stopPropagation();
                                    } },
                                    React.createElement("rect", { x: isLabelLeftOfEdge ? -labelWidth : 0, y: -labelHeight / 2, width: labelWidth, height: labelHeight, fill: "rgba(0, 0, 0, 0.001)" }),
                                    React.createElement("text", { className: "training-object-edge-label", textAnchor: labelAnchor, dominantBaseline: "middle" }, edge.labelSegments.map((segment, index) => (React.createElement("tspan", { key: `${edge.id}-${segment.text}-${index}`, fill: segment.color }, segment.text)))))))));
                    }),
                    nodes.map((node) => {
                        const position = positions[node.id];
                        if (position === undefined)
                            return null;
                        const isCurrentNode = currentNodeId === node.id;
                        const labelLines = splitLabel(node.label);
                        return (React.createElement("g", { key: node.id, transform: `translate(${position.x}, ${position.y})`, className: "training-stage-node", onMouseDown: (event) => {
                                if (event.button === 2) {
                                    setHasUserAdjustedView(true);
                                    dragStateRef.current = {
                                        type: "pan",
                                        pointerX: event.clientX,
                                        pointerY: event.clientY,
                                        startPanX: viewTransform.panX,
                                        startPanY: viewTransform.panY
                                    };
                                    return;
                                }
                                if (event.button !== 0)
                                    return;
                                const graphPoint = getGraphPoint(event.clientX, event.clientY, containerRef.current, viewTransform);
                                if (graphPoint === undefined)
                                    return;
                                setHasUserAdjustedView(true);
                                dragStateRef.current = {
                                    type: "node",
                                    nodeId: node.id,
                                    offsetX: position.x - graphPoint.x,
                                    offsetY: position.y - graphPoint.y
                                };
                                event.stopPropagation();
                            } },
                            React.createElement("circle", { r: nodeRadius, fill: node.fill, stroke: isCurrentNode ? "#111111" : "#474747", strokeWidth: isCurrentNode ? 5 : 2 }),
                            React.createElement("text", { className: "training-stage-node-label", textAnchor: "middle" }, labelLines.map((line, index) => (React.createElement("tspan", { key: `${node.id}-${line}-${index}`, x: 0, y: (index - (labelLines.length - 1) / 2) * 14 }, line))))));
                    }))))));
};
function getGraphPoint(clientX, clientY, container, viewTransform) {
    if (container === null)
        return undefined;
    const rect = container.getBoundingClientRect();
    return {
        x: (clientX - rect.left - viewTransform.panX) / viewTransform.scale,
        y: (clientY - rect.top - viewTransform.panY) / viewTransform.scale
    };
}
function computeFitViewTransform(nodes, positions, containerSize, nodeRadius) {
    const padding = 60;
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const node of nodes) {
        const position = positions[node.id];
        if (position === undefined)
            continue;
        minX = Math.min(minX, position.x - nodeRadius);
        minY = Math.min(minY, position.y - nodeRadius);
        maxX = Math.max(maxX, position.x + nodeRadius);
        maxY = Math.max(maxY, position.y + nodeRadius);
    }
    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
        return { panX: 0, panY: 0, scale: 1 };
    }
    const graphWidth = Math.max(1, maxX - minX);
    const graphHeight = Math.max(1, maxY - minY);
    const availableWidth = Math.max(1, containerSize.width - padding * 2);
    const availableHeight = Math.max(1, containerSize.height - padding * 2);
    const scale = clamp(Math.min(availableWidth / graphWidth, availableHeight / graphHeight), MIN_SCALE, 1.1);
    return {
        scale,
        panX: containerSize.width / 2 - ((minX + maxX) / 2) * scale,
        panY: containerSize.height / 2 - ((minY + maxY) / 2) * scale
    };
}
function splitLabel(label) {
    var _a;
    const sanitized = label.trim();
    if (sanitized.length <= 10)
        return [sanitized];
    const parts = sanitized.split(/[_\s-]+/).filter(Boolean);
    if (parts.length === 1) {
        return (_a = sanitized.match(/.{1,10}/g)) !== null && _a !== void 0 ? _a : [sanitized];
    }
    const lines = [];
    let currentLine = "";
    for (const part of parts) {
        if (currentLine.length === 0) {
            currentLine = part;
            continue;
        }
        if ((currentLine + part).length <= 10) {
            currentLine = `${currentLine} ${part}`;
            continue;
        }
        lines.push(currentLine);
        currentLine = part;
    }
    if (currentLine.length > 0)
        lines.push(currentLine);
    return lines.slice(0, 3);
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
